import { useRouter } from "next/router";
import { useContext, useEffect, useState } from "react";
import Head from "next/head";
import { GlobalContext } from "../../stores/use_global_state";
import Layout from "../../components/layout";
import getCommonLocales from "../../locales/locales_common";
import getErrorLocales from "../../locales/locales_error";
import getRemoveBgPageLocales from "../../locales/locales_remove_bg";
import dynamic from "next/dynamic";
import { fileExtensionList } from "../../constants/file_extension";
import { addToLocalStorage } from "../../utils/nonAuthLocalStorage";
import * as ga from "../../lib/ga";
import InteriorLayout from "../../components/interior-design-planner/InteriorLayout/index.jsx";
import { uploadMlFile } from "../api/apiMl";
import { gaCreditsLimit } from "../../utils/gaCreditsLimit";
const DynamicInteriorWrapper = dynamic(() => import("../../components/interior-design-planner/InteriorWrapper"), { ssr: false });
const AuthModal = dynamic(() => import("../../components/AuthModal/AuthModal"), { ssr: false });

export default function InteriorDesignPlanner({ image_url }) {
	const { locale, query } = useRouter();
	const { state, dispatch } = useContext(GlobalContext);
	const [showAuthModal, setShowAuthModal] = useState(false);
	const [uploadFileLoading, setUploadFileLoading] = useState(false);

	const checkNonAuth = () => {
		if (state.isAuth) return false;
		setShowAuthModal(true);
		addToLocalStorage("interior");
		return true;
	};

	const gaUploadImage = () => {
		ga.event({ action: `InteriorDesignPlanner page: upload image`, params: { event_category: "InteriorDesignPlanner page" } });
	};

	const uploadAsset = async (file) => {
		let token = state?.userInfo?.token ? state.userInfo.token : false;
		fetch(URL.createObjectURL(file))
			.then((res) => res.blob())
			.then(async (blob) => {
				let imgFile = new File([blob], "img.png", { type: blob.type });
				const { data } = await uploadMlFile(imgFile, token);
				dispatch({ type: "setInteriorRedesignImg", imageData: { image: data.file_url, id: data.id } });
				setUploadFileLoading(false);
			});
	};

	const uploadFile = async (files) => {
		setUploadFileLoading(true);
		gaUploadImage();

		if (state.isAuth) {
			if (state?.accountData?.plan === "free" && state?.accountData?.total_credits < 1) {
				gaCreditsLimit("INTERIOR");
				dispatch({ type: "setVideoError", error_msg: "free_limit_img", error_code: "free_limit_img" });
				setUploadFileLoading(false);

				return false;
			}

			if ((state?.accountData?.plan === "pro" || state?.accountData?.plan === "promo") && state?.accountData?.total_credits < 1) {
				gaCreditsLimit("INTERIOR");
				dispatch({ type: "setVideoError", error_msg: "pro_limit", error_code: "pro_limit" });
				setUploadFileLoading(false);

				return false;
			}
		}

		let image = files[0];

		let fileExtension = image.name.split(".").pop();
		const ExtensionList = new Set(fileExtensionList);

		const errorMsg = dictionary["Extension .XX is not allowed. Only .mp4, .mov, .ts, .avi, .mkv, .webm, .mpg, .jpg, .png are allowed"]
			.replace("XX", fileExtension)
			.replace(" .mp4, .mov, .mkv, .webm,", "");

		if (fileExtension.toLowerCase() === "heif" || fileExtension.toLowerCase() === "heic") {
			const heic2any = (await import("heic2any")).default;

			fetch(URL.createObjectURL(image))
				.then((res) => {
					return res.blob();
				})
				.then((blob) => {
					return heic2any({ blob });
				})
				.then((conversionResult) => {
					const url = URL.createObjectURL(conversionResult);

					dispatch({ type: "setInteriorRedesignImg", image: url });
					setUploadFileLoading(false);
				})
				.catch((e) => {
					dispatch({ type: "setVideoError", error_msg: errorMsg, error_code: "extension" });
					setUploadFileLoading(false);
				});
			return;
		}

		if (!ExtensionList.has(fileExtension.toLowerCase())) {
			dispatch({ type: "setVideoError", error_msg: errorMsg, error_code: "extension" });
			setUploadFileLoading(false);

			return false;
		}
		uploadAsset(image);
		dispatch({ type: "setInteriorRedesignTask", task: null });
		dispatch({ type: "setInteriorRedesignResults", results: [] });
	};
	const dictionary = { ...getCommonLocales(locale), ...getErrorLocales(locale), ...getRemoveBgPageLocales(locale) };

	useEffect(() => {
		return () => {
			setUploadFileLoading(false);
		};
	}, []);

	useEffect(
		() => {
			if (query.file && state?.temporaryFileStorage) {
				uploadFile(state.temporaryFileStorage);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[query]
	);

	const pageLink = image_url ? `https://bgrem.ai/interior-design-planner/?image_url=${image_url}` : "https://bgrem.ai/interior-design-planner/";

	let title = dictionary["page_interior_title"] || "AI Interior Design: AI Room Planner Online - BgRem";
	let description = dictionary["page_interior_description"] || "Get amazing AI interior design ideas in a few clicks";
	let ogTitle = title;
	let ogDescription = description;

	const getCanonicalLink = (locale) => {
		const enList = ["en", "ja", "ar"];

		if (enList.includes(locale)) {
			return `https://bgrem.ai/interior-design-planner/`;
		} else {
			return `https://bgrem.ai/${locale}/interior-design-planner/`;
		}
	};

	return (
		<Layout mainClassName={`remove_bg magic_bg avatar-layout`}>
			<Head>
				<title>{title}</title>
				<meta name="description" content={description} />
				<meta property="og:type" content="website" />
				<meta property="og:title" content={ogTitle} />
				<meta property="og:description" content={ogDescription} />
				<meta property="og:url" content={pageLink} />
				<meta property="og:image" content={image_url ? image_url : "https://bgrem.ai/seo/og-styling.jpg"} />
				<meta property="og:site_name" content="bgrem" />
				<meta property="og:locale" content="en_US" />
				<meta property="og:section" content="Turn picture into painting with AI" />
				<meta name="twitter:title" content={ogTitle} />
				<meta name="twitter:description" content={ogDescription} />
				<meta property="twitter:card" content="summary_large_image" />
				<meta property="twitter:image:src" content={image_url ? image_url : "https://bgrem.ai/seo/og-styling.jpg"} />
				<meta name="twitter:image" content={image_url ? image_url : "https://bgrem.ai/seo/og-styling.jpg"} />
				<link rel="canonical" href={getCanonicalLink(locale)} />
			</Head>

			{state?.interiorRedesignImg !== null ? (
				<DynamicInteriorWrapper dictionary={dictionary} uploadFile={uploadFile} />
			) : (
				<InteriorLayout dictionary={dictionary} uploadFile={uploadFile} isLoading={uploadFileLoading} checkNonAuth={checkNonAuth} />
			)}

			{showAuthModal && <AuthModal closeHandler={() => setShowAuthModal(false)} serviceName={"InteriorRedesign"} />}
		</Layout>
	);
}

export async function getServerSideProps(context) {
	const image_url = context.query.image_url || null;
	return {
		props: { image_url },
	};
}
